
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line

/**
 * DesiredClientAppSettings struct
 */
class DesiredClientAppSettings {
  /**
   * Initialize struct
   * @param {string=} latestApkFileUrl
   * @param {string=} latestAppVersion
   * @param {number=} nonForceUpdateTimeInterval
   * @constructor
   */
  constructor (latestApkFileUrl = null, latestAppVersion = null, nonForceUpdateTimeInterval = null) {
    this.LatestApkFileUrl = latestApkFileUrl
    this.LatestAppVersion = latestAppVersion
    this.NonForceUpdateTimeInterval = nonForceUpdateTimeInterval
  }

  /**
   * Copy struct (shallow copy)
   * @this {!DesiredClientAppSettings}
   * @param {!DesiredClientAppSettings} other Other struct
   * @returns {!DesiredClientAppSettings} This struct
   */
  copy (other) {
    if (other.LatestApkFileUrl != null) {
      this.LatestApkFileUrl = other.LatestApkFileUrl
    } else {
      this.LatestApkFileUrl = null
    }
    if (other.LatestAppVersion != null) {
      this.LatestAppVersion = other.LatestAppVersion
    } else {
      this.LatestAppVersion = null
    }
    if (other.NonForceUpdateTimeInterval != null) {
      this.NonForceUpdateTimeInterval = other.NonForceUpdateTimeInterval
    } else {
      this.NonForceUpdateTimeInterval = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!DesiredClientAppSettings}
   * @returns {!DesiredClientAppSettings} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new DesiredClientAppSettingsModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new DesiredClientAppSettingsModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!DesiredClientAppSettings}
   * @param {!DesiredClientAppSettings} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof DesiredClientAppSettings)) {
      throw new TypeError('Instance of DesiredClientAppSettings is required!')
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!DesiredClientAppSettings}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    return {
      LatestApkFileUrl: ((this.LatestApkFileUrl != null) ? this.LatestApkFileUrl : null),
      LatestAppVersion: ((this.LatestAppVersion != null) ? this.LatestAppVersion : null),
      NonForceUpdateTimeInterval: ((this.NonForceUpdateTimeInterval != null) ? this.NonForceUpdateTimeInterval : null)
    }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return DesiredClientAppSettings.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!DesiredClientAppSettings} other Object value
   * @returns {!DesiredClientAppSettings} Created struct
   */
  static fromObject (other) {
    return new DesiredClientAppSettings().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!DesiredClientAppSettings}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return DesiredClientAppSettings.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!DesiredClientAppSettings}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 215
  }
}

export { DesiredClientAppSettings };

/**
 * Fast Binary Encoding DesiredClientAppSettings field model
 */
class FieldModelDesiredClientAppSettings extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this.LatestApkFileUrl = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, 4 + 4), buffer, 4 + 4)
    this.LatestAppVersion = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.LatestApkFileUrl.fbeOffset + this.LatestApkFileUrl.fbeSize), buffer, this.LatestApkFileUrl.fbeOffset + this.LatestApkFileUrl.fbeSize)
    this.NonForceUpdateTimeInterval = new fbe.FieldModelOptional(new fbe.FieldModelUInt32(buffer, this.LatestAppVersion.fbeOffset + this.LatestAppVersion.fbeSize), buffer, this.LatestAppVersion.fbeOffset + this.LatestAppVersion.fbeSize)
  }

  /**
   * Get the field size
   * @this {!FieldModelDesiredClientAppSettings}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelDesiredClientAppSettings}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.LatestApkFileUrl.fbeSize + this.LatestAppVersion.fbeSize + this.NonForceUpdateTimeInterval.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelDesiredClientAppSettings}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.LatestApkFileUrl.fbeExtra + this.LatestAppVersion.fbeExtra + this.NonForceUpdateTimeInterval.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelDesiredClientAppSettings}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelDesiredClientAppSettings.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelDesiredClientAppSettings}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 215
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelDesiredClientAppSettings}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelDesiredClientAppSettings}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.LatestApkFileUrl.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.LatestApkFileUrl.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.LatestApkFileUrl.fbeSize

    if ((fbeCurrentSize + this.LatestAppVersion.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.LatestAppVersion.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.LatestAppVersion.fbeSize

    if ((fbeCurrentSize + this.NonForceUpdateTimeInterval.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.NonForceUpdateTimeInterval.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NonForceUpdateTimeInterval.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelDesiredClientAppSettings}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelDesiredClientAppSettings}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelDesiredClientAppSettings}
   * @param {!DesiredClientAppSettings} fbeValue Default value, defaults is new DesiredClientAppSettings()
   * @returns {!DesiredClientAppSettings} DesiredClientAppSettings value
   */
  get (fbeValue = new DesiredClientAppSettings()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelDesiredClientAppSettings}
   * @param {!DesiredClientAppSettings} fbeValue DesiredClientAppSettings value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.LatestApkFileUrl.fbeSize) <= fbeStructSize) {
      fbeValue.LatestApkFileUrl = this.LatestApkFileUrl.get()
    } else {
      fbeValue.LatestApkFileUrl = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.LatestApkFileUrl.fbeSize

    if ((fbeCurrentSize + this.LatestAppVersion.fbeSize) <= fbeStructSize) {
      fbeValue.LatestAppVersion = this.LatestAppVersion.get()
    } else {
      fbeValue.LatestAppVersion = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.LatestAppVersion.fbeSize

    if ((fbeCurrentSize + this.NonForceUpdateTimeInterval.fbeSize) <= fbeStructSize) {
      fbeValue.NonForceUpdateTimeInterval = this.NonForceUpdateTimeInterval.get()
    } else {
      fbeValue.NonForceUpdateTimeInterval = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.NonForceUpdateTimeInterval.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelDesiredClientAppSettings}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelDesiredClientAppSettings}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelDesiredClientAppSettings}
   * @param {!DesiredClientAppSettings} fbeValue DesiredClientAppSettings value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelDesiredClientAppSettings}
   * @param {DesiredClientAppSettings} fbeValue DesiredClientAppSettings value
   */
  setFields (fbeValue) {
    this.LatestApkFileUrl.set(fbeValue.LatestApkFileUrl)
    this.LatestAppVersion.set(fbeValue.LatestAppVersion)
    this.NonForceUpdateTimeInterval.set(fbeValue.NonForceUpdateTimeInterval)
  }
}

export { FieldModelDesiredClientAppSettings };

/**
 * Fast Binary Encoding DesiredClientAppSettings model
 */
class DesiredClientAppSettingsModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelDesiredClientAppSettings(this.buffer, 4)
  }

  /**
   * Get the DesiredClientAppSettings model
   * @this {!DesiredClientAppSettingsModel}
   * @returns {!FieldModelDesiredClientAppSettings} model DesiredClientAppSettings model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!DesiredClientAppSettingsModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!DesiredClientAppSettingsModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return DesiredClientAppSettingsModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!DesiredClientAppSettingsModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelDesiredClientAppSettings.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!DesiredClientAppSettingsModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!DesiredClientAppSettingsModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!DesiredClientAppSettingsModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!DesiredClientAppSettingsModel}
   * @param {!DesiredClientAppSettings} value DesiredClientAppSettings value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!DesiredClientAppSettingsModel}
   * @param {!DesiredClientAppSettings} value DesiredClientAppSettings value, defaults is new DesiredClientAppSettings()
   * @return {!object} Deserialized DesiredClientAppSettings value and its size
   */
  deserialize (value = new DesiredClientAppSettings()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new DesiredClientAppSettings(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new DesiredClientAppSettings(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!DesiredClientAppSettingsModel}
   * @param {!number} prev Previous DesiredClientAppSettings model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { DesiredClientAppSettingsModel };
